import { useEffect, useRef, useState } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import {
  Box,
  useBreakpointValue,
  usePrefersReducedMotion,
} from "@chakra-ui/react";

export const MotionBox = motion(Box);

export const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

export const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } },
};

export const containerStagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
      when: "beforeChildren",
    },
  },
};

export const itemFadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

/**
 * @typedef {object} Props
 * @property {import("react").ReactNode} children
 * @property {object} variants
 * @property {number} [threshold]
 * @property {number} [thresholdMobile]
 * @property {boolean} [once]
 * @property {string} [initial]
 * @property {string} [visible]
 * @property {import("react").CSSProperties} [style]
 */

/**
 * Reveal component to animate children when they come into view
 * @param {Props} props
 */
export function Reveal({
  children,
  variants,
  threshold = 0.3,
  thresholdMobile = 0.1,
  once = true,
  initial = "hidden",
  visible = "visible",
  style,
  ...props
}) {
  const controls = useAnimation();
  const ref = useRef(null);

  const [isJsEnabled, setIsJsEnabled] = useState(false);

  useEffect(() => {
    setIsJsEnabled(true);
  }, []);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const inView = useInView(ref, {
    amount: isMobile ? thresholdMobile : threshold,
  });
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      controls.set(visible);
      return;
    }
    if (inView) {
      controls.start(visible);
    } else if (!once) {
      controls.start(initial);
    }
  }, [controls, inView, once, initial, visible, prefersReducedMotion]);

  return (
    <>
      <MotionBox
        ref={ref}
        display={isJsEnabled ? "block" : "none"}
        initial={initial}
        animate={controls}
        variants={variants}
        style={style}
        {...props}>
        {children}
      </MotionBox>

      {!isJsEnabled && <noscript>{children}</noscript>}
    </>
  );
}
